import { twJoin, twMerge } from '@troon/tailwind-preset/merge';
import { Dynamic } from 'solid-js/web';
import { splitProps } from 'solid-js';
import type { ComponentProps, ValidComponent } from 'solid-js';

type Props<T extends ValidComponent> = ComponentProps<T> & {
	as?: T;
	class?: string;
	appearance?:
		| 'neutral'
		| 'access'
		| 'info'
		| 'danger'
		| 'positive'
		| 'silver'
		| 'gold'
		| 'platinum'
		| 'hot'
		| 'pending';
	rounded?: boolean;
};

export function Tag<T extends ValidComponent>(props: Props<T>) {
	const [, rest] = splitProps(props, ['as', 'class', 'type']);
	return (
		<Dynamic
			component={props.as ?? 'span'}
			class={twMerge(
				// TODO: consider adding silver, gold, and platinum colors to tailwind config if we reuse these.

				twJoin(
					'inline-flex w-fit items-center gap-1 px-2 py-1 text-xs font-semibold text-nowrap uppercase',
					props.appearance === 'access' && 'bg-gradient-to-r from-neutral-950 to-brand-700 text-white',
					props.appearance === 'neutral' && 'bg-neutral-200 text-neutral-800',
					props.appearance === 'positive' && 'bg-green-100 text-green-600',
					(!props.appearance || props.appearance === 'info') && 'bg-brand-100 text-brand-600',
					props.appearance === 'danger' && 'bg-red-100 text-red-600',
					props.appearance === 'silver' && 'bg-gradient-to-r from-neutral-300 to-neutral-600',
					props.appearance === 'gold' && 'bg-gradient-to-r from-neutral-300 to-[#E6C885]',
					props.appearance === 'platinum' && 'bg-gradient-to-r from-neutral-300 to-[#BDC3D3]',
					props.appearance === 'hot' && 'bg-gradient-to-b from-orange-500 to-orange-600 text-white',
					props.appearance === 'pending' && 'bg-orange-100 text-orange-700',

					props.rounded ? 'rounded-full' : 'rounded-md',
				),

				props.class,
			)}
			{...rest}
		>
			{props.children}
		</Dynamic>
	);
}
